import { ReactElement, useEffect, useId, useState, SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { PopperPlacementType } from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

interface Props {
  content: ReactElement;
  children: ReactElement;
  placement?: PopperPlacementType;
  onHover?: () => void;
}

const RICH_TOOLTIP_OPEN_EVENT = 'rich-tooltip-open';

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 2000,
  '&[data-popper-placement*="bottom"] #arrow': {
    top: 0,
    left: 0,
    marginTop: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
    },
  },
  '&[data-popper-placement*="top"] #arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
    },
  },
  '&[data-popper-placement*="right"] #arrow': {
    left: 0,
    marginLeft: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
    },
  },
  '&[data-popper-placement*="left"] #arrow': {
    right: 0,
    marginRight: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
    },
  },
}));

const Arrow = styled('span')({
  overflow: 'hidden',
  position: 'absolute',
  fontSize: 7,
  width: '3em',
  height: '3em',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  },
});

const TooltipBtn = styled('div')({ '.MuiSvgIcon > *': { width: 24, height: 24 } });

function RichTooltip({ placement = 'bottom', content, children, onHover = () => {} }: Props) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
  const [childNode, setChildNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const handleOtherTooltipOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      if (customEvent.detail !== tooltipId) {
        setOpen(false);
      }
    };

    window.addEventListener(RICH_TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen as EventListener);
    return () => {
      window.removeEventListener(RICH_TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen as EventListener);
    };
  }, [tooltipId]);

  const handleToggle = (e: SyntheticEvent<HTMLElement>) => {
    setChildNode(e.currentTarget);
    setOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        window.dispatchEvent(new CustomEvent(RICH_TOOLTIP_OPEN_EVENT, { detail: tooltipId }));
      }
      return nextOpen;
    });
  };

  const handleOpen = (e: SyntheticEvent<HTMLElement>, newOpen: boolean) => {
    setChildNode(e.currentTarget);
    if (newOpen) {
      window.dispatchEvent(new CustomEvent(RICH_TOOLTIP_OPEN_EVENT, { detail: tooltipId }));
    }
    setOpen(newOpen);
  };

  return (
    <div>
      <TooltipBtn
        role="button"
        onFocus={(e) => {
          handleOpen(e, true);
          onHover();
        }}
        onMouseOver={(e) => {
          handleOpen(e, true);
          onHover();
        }}
        onBlur={(e) => {
          handleOpen(e, false);
        }}
        tabIndex={0}
        onKeyDown={handleToggle}
        onClick={handleToggle}
      >
        {children}
      </TooltipBtn>
      <StyledPopper
        anchorEl={childNode}
        open={open}
        placement={placement}
        transition
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper>
              <Arrow id="arrow" ref={setArrowRef} />
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                }}
              >
                <Box padding={2}>{content}</Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </StyledPopper>
    </div>
  );
}

export default RichTooltip;
